import React from "react";
import { motion } from "framer-motion";
import { UserPlus, Droplets, ThumbsUp, ArrowRight } from "lucide-react";
import { howItWorks, LOGO_TRANSPARENT } from "../data/mock";

const stepIcons = [UserPlus, Droplets, ThumbsUp];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-40 right-0 w-80 h-80 bg-[#16C79A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#16C79A]/5 rounded-full blur-3xl" />

      {/* Floating mascot - center right */}
      <motion.img
        src={LOGO_TRANSPARENT}
        alt=""
        aria-hidden="true"
        className="absolute top-1/2 -right-16 w-56 opacity-[0.05] rotate-6 pointer-events-none select-none hidden xl:block"
        animate={{ y: [-10, 10, -10], rotate: [6, 8, 6] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <span className="inline-block text-[#16C79A] text-sm font-bold uppercase tracking-[0.2em] mb-4">
            Simple Process
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0C2D48] mb-6">
            How It <span className="text-[#16C79A]">Works</span>
          </h2>
          <p className="text-lg text-slate-500">
            Getting your bins professionally cleaned has never been easier.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {howItWorks.map((step, index) => {
            const Icon = stepIcons[index];
            return (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative group"
              >
                {/* Connector line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-[2px]">
                    <div className="w-full h-full bg-gradient-to-r from-[#16C79A]/30 to-transparent" />
                    <ArrowRight
                      size={16}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-[#16C79A]/30"
                    />
                  </div>
                )}

                <div className="text-center">
                  {/* Step Number + Icon */}
                  <div className="relative inline-flex mb-8">
                    <div className="w-28 h-28 rounded-3xl bg-[#f0faf7] border border-[#e0f5ef] flex items-center justify-center group-hover:bg-[#16C79A]/5 group-hover:border-[#16C79A]/20 transition-all duration-500">
                      <Icon
                        size={40}
                        strokeWidth={1.5}
                        className="text-[#16C79A] group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute -top-3 -right-3 w-9 h-9 rounded-xl bg-[#0C2D48] text-white flex items-center justify-center text-sm font-bold shadow-lg shadow-[#0C2D48]/20">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-[#0C2D48] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed max-w-xs mx-auto">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
